import { Tooltip } from "@indigo-components";
import { useQuery } from "@tanstack/react-query";
import { type ComponentProps, useEffect, useState } from "react";
import { getUploadedFileOptimized } from "~/api/graphql-custom/queries";
import { graphqlClient } from "~/lib/graphql-client";
import { parseAppointmentDocHref } from "./handlers/appointmentDocument";
import type { ChatbotLinkContext } from "./types";

type Props = ComponentProps<"a"> & { href: string; ctx: ChatbotLinkContext };

export function AppointmentDocumentLink({
  href,
  ctx,
  children,
  ...rest
}: Props) {
  const request = parseAppointmentDocHref(href);
  const uploadedFileId = request?.uploadedFileId;
  const [openFailure, setOpenFailure] = useState<{
    href: string;
    message: string;
  } | null>(null);
  const [hovered, setHovered] = useState(false);
  const availability = useQuery({
    queryKey: ["chatbot-document-availability", uploadedFileId],
    enabled: !!uploadedFileId && !ctx.appointmentDocumentDisabledReason,
    queryFn: async () => {
      if (!uploadedFileId) return false;
      const result = await graphqlClient.graphql({
        query: getUploadedFileOptimized,
        variables: { id: uploadedFileId },
      });
      return !!result.data?.getUploadedFile?.key;
    },
    retry: 1,
  });

  useEffect(() => {
    if (!hovered) return;
    const timeout = window.setTimeout(() => setHovered(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [hovered]);

  let disabledReason =
    ctx.appointmentDocumentDisabledReason ??
    (openFailure?.href === href ? openFailure.message : null);
  if (!disabledReason && !request)
    disabledReason = "This document link is invalid.";
  if (!disabledReason && uploadedFileId) {
    if (availability.isPending) disabledReason = "Checking file availability…";
    else if (availability.isError)
      disabledReason = "Could not check this file. Please try again later.";
    else if (!availability.data)
      disabledReason =
        "This file could not be found. It may have been removed.";
  }

  return (
    <Tooltip
      content={disabledReason}
      visible={hovered && !!disabledReason}
      placement="top"
    >
      <a
        {...rest}
        href={disabledReason ? undefined : href}
        role="link"
        tabIndex={0}
        aria-disabled={!!disabledReason}
        data-chatbot-link-handler="appointment-document"
        style={
          disabledReason
            ? {
                color: "var(--indigo-text-tertiary)",
                cursor: "default",
                textDecoration: "none",
              }
            : undefined
        }
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={(event) => {
          if (disabledReason) {
            event.preventDefault();
            return;
          }
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            return;
          event.preventDefault();
          if (request) {
            ctx.onRequestAppointmentDocument?.({
              ...request,
              onUnavailable: (message) => setOpenFailure({ href, message }),
            });
          }
        }}
      >
        {children}
      </a>
    </Tooltip>
  );
}
